// src/pages/WaterMeterDetailPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  TextField,
  Modal,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api, { subscribeEvents } from '../services/api';
import { formatDate, formatConsumption } from '../utils/format';

const PAGE_SIZE = 20;
const STATUS_POLL_INTERVAL_MS = 5000;
const OBJECT_ID_REGEX = /^[0-9a-fA-F]{24}$/;
const HEX_STRING_REGEX = /^(?:[0-9a-fA-F]{2})+$/;

export default function WaterMeterDetailPage() {
  const { meterId: paramMeterId } = useParams();
  const [realId, setRealId] = useState(paramMeterId.trim());
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const [waterMeter, setWaterMeter] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    gpsCoordinates: '',
    initPairingKey: '',
    password: ''
  });
  const [formErrors, setFormErrors] = useState({
    initPairingKey: '',
    password: ''
  });
  const [shutdownModalOpen, setShutdownModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [resetConsumptionModalOpen, setResetConsumptionModalOpen] = useState(false);

  const [consumptions, setConsumptions] = useState([]);
  const [consumpStart, setConsumpStart] = useState(null);
  const [loadingMoreConsumptions, setLoadingMoreConsumptions] = useState(false);

  const [alerts, setAlerts] = useState([]);

  const [bleActivities, setBleActivities] = useState([]);

  const [gwActs, setGwActs] = useState([]);
  const [gwStart, setGwStart] = useState(null);
  const [loadingMoreGw, setLoadingMoreGw] = useState(false);

  const [selectedGw, setSelectedGw] = useState(null);
  const [subGwActs, setSubGwActs] = useState([]);
  const [subStart, setSubStart] = useState(null);
  const [loadingMoreSub, setLoadingMoreSub] = useState(false);

  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // AbortController refs
  const fetchMeterRef = useRef();
  const statusRef = useRef();
  const consumptionsRef = useRef();
  const alertsRef = useRef();
  const bleRef = useRef();
  const gwActsRef = useRef();
  const subGwActsRef = useRef();
  const shutdownReqRef = useRef();
  const saveRef = useRef();
  const shutdownRespRef = useRef();
  const deleteRef = useRef();
  const statusIntervalRef = useRef(null);

  const formatGps = (value) => {
    const parts = String(value)
      .split(',')
      .map(c => parseFloat(c.trim()));
    if (parts.length === 2 && parts.every(n => !isNaN(n))) {
      return `${parts[0].toFixed(6)}, ${parts[1].toFixed(6)}`;
    }
    return value;
  };

  const resetForm = () => {
    const raw = waterMeter?.gpsCoordinates || '';
    const parts = String(raw)
      .split(',')
      .map(c => parseFloat(c.trim()));
    const formatted = (parts.length === 2 && parts.every(n => !isNaN(n)))
      ? `${parts[0].toFixed(6)}, ${parts[1].toFixed(6)}`
      : '';

    setFormData({
      gpsCoordinates: formatted,
      initPairingKey: '',
      password: ''
    });
    setFormErrors({
      initPairingKey: '',
      password: ''
    });
  };

  const handleEdit = () => {
    resetForm();
    setEditMode(true);
  };

  // Resolve the “real” ID
  const fetchWaterMeter = async () => {
    setError(null);
    fetchMeterRef.current?.abort();
    const controller = new AbortController();
    fetchMeterRef.current = controller;

    try {
      const raw = paramMeterId.trim();
      let res;
      if (OBJECT_ID_REGEX.test(raw)) {
        res = await api.get(`/api/proxy/wmeters/${raw}`, { signal: controller.signal });
        setRealId(raw);
      } else {
        const serial = raw.replace(/^W-/, '');
        res = await api.get(`/api/proxy/wmeters/serial/${serial}`, { signal: controller.signal });
        setRealId(res.data._id?.$oid || res.data._id);
      }

      const wm = res.data;
      setWaterMeter(wm);

      const coords = String(wm.gpsCoordinates || '')
        .split(',')
        .map(c => parseFloat(c.trim()));
      const formatted = (coords.length === 2 && coords.every(n => !isNaN(n)))
        ? `${coords[0].toFixed(6)}, ${coords[1].toFixed(6)}`
        : '';

      setFormData({
        gpsCoordinates: formatted,
        initPairingKey: '',
        password: ''
      });
      setFormErrors({
        initPairingKey: '',
        password: ''
      });
    } catch (err) {
      if (err.name !== 'CanceledError') {
        console.error(err);
        setError('Failed to load water meter.');
      }
    }
  };

  const fetchWaterMeterStatus = async () => {
    if (!OBJECT_ID_REGEX.test(realId)) return;

    statusRef.current?.abort();
    const controller = new AbortController();
    statusRef.current = controller;

    try {
      const res = await api.get(`/api/proxy/wmeters/status/${realId}`, {
        signal: controller.signal
      });

      setWaterMeter(prev => (prev ? { ...prev, ...res.data } : prev));
    } catch (err) {
      if (err.name !== 'CanceledError') console.error(err);
    }
  };

  // Fetch sub-resources once realId is ready
  const fetchConsumptions = async (start) => {
    if (start) setLoadingMoreConsumptions(true);
    consumptionsRef.current?.abort();
    const controller = new AbortController();
    consumptionsRef.current = controller;
    const params = { max: PAGE_SIZE };
    if (start) params.start = start.toISOString();

    try {
      const res = await api.get(`/api/proxy/wmeters/consumption/${realId}`, {
        params,
        signal: controller.signal
      });
      const items = res.data;
      setConsumptions(prev => (start ? [...prev, ...items] : items));
      if (items.length) setConsumpStart(new Date(items[items.length - 1].timestamp));
    } catch (err) {
      if (err.name !== 'CanceledError') console.error(err);
    } finally {
      if (start) setLoadingMoreConsumptions(false);
    }
  };

  const fetchAlerts = async () => {
    alertsRef.current?.abort();
    const controller = new AbortController();
    alertsRef.current = controller;
    try {
      const res = await api.get(`/api/proxy/wmeters/critical/${realId}`, { signal: controller.signal });
      const sorted = res.data.sort((a, b) =>
        (b.status === 'raised') - (a.status === 'raised') ||
        new Date(b.receivedAt) - new Date(a.receivedAt)
      );
      setAlerts(sorted);
    } catch (err) {
      if (err.name !== 'CanceledError') console.error(err);
    }
  };

  const fetchBle = async () => {
    bleRef.current?.abort();
    const controller = new AbortController();
    bleRef.current = controller;
    try {
      const res = await api.get(`/api/proxy/wmeters/ble-activity/${realId}`, { signal: controller.signal });
      setBleActivities(res.data);
    } catch (err) {
      if (err.name !== 'CanceledError') console.error(err);
    }
  };

  const fetchGwActs = async (start) => {
    if (start) setLoadingMoreGw(true);
    gwActsRef.current?.abort();
    const controller = new AbortController();
    gwActsRef.current = controller;
    const params = { max: PAGE_SIZE };
    if (start) params.start = start.toISOString();

    try {
      const res = await api.get(`/api/proxy/wmeters/gateway-activity/${realId}`, {
        params,
        signal: controller.signal
      });
      const items = res.data;
      setGwActs(prev => (start ? [...prev, ...items] : items));
      if (items.length) setGwStart(new Date(items[items.length - 1].timestamp));
    } catch (err) {
      if (err.name !== 'CanceledError') console.error(err);
    } finally {
      if (start) setLoadingMoreGw(false);
    }
  };

  const fetchSubGwActs = async (gatewayId, start) => {
    if (start) setLoadingMoreSub(true);
    subGwActsRef.current?.abort();
    const controller = new AbortController();
    subGwActsRef.current = controller;
    const params = { max: PAGE_SIZE, gatewayId };
    if (start) params.start = start.toISOString();

    try {
      const res = await api.get(`/api/proxy/wmeters/gateway-activity/${realId}`, {
        params,
        signal: controller.signal
      });
      const items = res.data;
      setSubGwActs(prev => (start ? [...prev, ...items] : items));
      if (items.length) setSubStart(new Date(items[items.length - 1].timestamp));
    } catch (err) {
      if (err.name !== 'CanceledError') console.error(err);
    } finally {
      if (start) setLoadingMoreSub(false);
    }
  };

  /* Opens the modal when the meter has a request still waiting for an answer.
   * A meter with nothing pending is the normal case, not an error. */
  const fetchShutdownRequest = async () => {
    shutdownReqRef.current?.abort();
    const controller = new AbortController();
    shutdownReqRef.current = controller;
    try {
      const res = await api.get(`/api/proxy/wmeters/shutdown-requests/${realId}`, {
        signal: controller.signal
      });
      if (res.data.status === 'requested') {
        setShutdownModalOpen(true);
      }
    } catch (err) {
      if (err.name === 'CanceledError' || err.response?.status === 404) return;
      console.error('Error fetching shutdown request:', err);
    }
  };

  const handleGwRowClick = (gatewayId) => {
    setSelectedGw(gatewayId);
    setSubGwActs([]);
    setSubStart(null);
    fetchSubGwActs(gatewayId, null);
  };

  const handleSave = async () => {
    saveRef.current?.abort();
    const controller = new AbortController();
    saveRef.current = controller;

    const nextErrors = {
      initPairingKey: '',
      password: ''
    };

    const gpsInput = formData.gpsCoordinates.trim();
    const coords = gpsInput
      ? gpsInput.split(',').map(c => parseFloat(c.trim())).filter(n => !isNaN(n))
      : [];

    if (gpsInput && coords.length !== 2) {
      setSnackbar({
        open: true,
        message: 'GPS coordinates must be in the format "longitude, latitude"',
        severity: 'error'
      });
      return;
    }

    const authKey = formData.initPairingKey.trim();
    const authPassword = formData.password;
    const hasAnyAuthInput = authKey !== '' || authPassword !== '';

    if (hasAnyAuthInput) {
      if (!HEX_STRING_REGEX.test(authKey)) {
        nextErrors.initPairingKey = 'Pairing key must be a valid hex string';
      }
      if (!authPassword) {
        nextErrors.password = 'Password is required';
      }
    }

    if (nextErrors.initPairingKey || nextErrors.password) {
      setFormErrors(nextErrors);
      return;
    }

    try {
      const originalCoords = Array.isArray(waterMeter.gpsCoordinates)
        ? waterMeter.gpsCoordinates
        : [];

      const gpsChanged =
        coords.length === 2 &&
        (
          !originalCoords.length ||
          coords[0] !== originalCoords[0] ||
          coords[1] !== originalCoords[1]
        );

      if (!gpsChanged && !hasAnyAuthInput) {
        setEditMode(false);
        return;
      }

      let updatedMeter = waterMeter;

      if (gpsChanged) {
        const res = await api.patch(
          `/api/proxy/wmeters/${realId}`,
          { gpsCoordinates: coords },
          { signal: controller.signal }
        );

        updatedMeter = {
          ...res.data,
          criticalState: waterMeter.criticalState,
          deviceStatus: waterMeter.deviceStatus
        };
      }

      if (hasAnyAuthInput) {
        await api.patch(
          `/api/proxy/wmeters/auth/${realId}`,
          {
            initPairingKey: authKey,
            password: authPassword
          },
          { signal: controller.signal }
        );
      }

      if (gpsChanged) {
        setWaterMeter(updatedMeter);
      }

      setEditMode(false);
      setSnackbar({
        open: true,
        message:
          gpsChanged && hasAnyAuthInput
            ? 'Meter details and auth saved successfully'
            : hasAnyAuthInput
              ? 'Meter auth saved successfully'
              : 'Saved successfully',
        severity: 'success'
      });
    } catch (err) {
      if (err.name !== 'CanceledError') {
        console.error(err);
        setSnackbar({
          open: true,
          message: err.response?.status === 403
            ? 'Save failed: Action denied'
            : 'Save failed',
          severity: 'error'
        });
      }
    }
  };

  const handleShutdownResponse = async (status) => {
    shutdownRespRef.current?.abort();
    const controller = new AbortController();
    shutdownRespRef.current = controller;
    try {
      await api.patch(`/api/proxy/wmeters/shutdown-requests/${realId}`, { status }, {
        signal: controller.signal
      });
      setShutdownModalOpen(false);
      setSnackbar({ open: true, message: 'Shutdown response sent', severity: 'info' });
    } catch (err) {
      if (err.name !== 'CanceledError') {
        if (err.response?.status === 404) {
          setShutdownModalOpen(false);
          setSnackbar({ open: true, message: 'Shutdown request has already expired', severity: 'warning' });
        } else {
          console.error(err);
          setSnackbar({
            open: true,
            message: err.response?.status === 403
              ? 'Failed to send shutdown response: Action denied'
              : 'Failed to send shutdown response',
            severity: 'error'
          });
        }
      }
    }
  };

  const handleDeleteClick = () => setDeleteModalOpen(true);
  const handleConfirmDelete = async () => {
    deleteRef.current?.abort();
    const controller = new AbortController();
    deleteRef.current = controller;
    try {
      await api.delete(`/api/proxy/wmeters/${realId}`, {
        signal: controller.signal
      });
      navigate(-1);
    } catch (err) {
      if (err.name !== 'CanceledError') {
        console.error(err);
        setSnackbar({
          open: true,
          message: err.response?.status === 403
            ? 'Delete failed: Action denied'
            : 'Delete failed',
          severity: 'error'
        });
      }
    }
  };

  useEffect(() => {
    fetchWaterMeter();
    return () => {
      fetchMeterRef.current?.abort();
    };
  }, [paramMeterId]);

  useEffect(() => {
    if (!OBJECT_ID_REGEX.test(realId)) return;

    fetchWaterMeterStatus();
    statusIntervalRef.current = setInterval(fetchWaterMeterStatus, STATUS_POLL_INTERVAL_MS);

    return () => {
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
        statusIntervalRef.current = null;
      }
      statusRef.current?.abort();
    };
  }, [realId]);

  useEffect(() => {
    if (!OBJECT_ID_REGEX.test(realId)) return;

    fetchConsumptions(null);
    fetchAlerts();
    fetchBle();
    fetchGwActs(null);
    fetchShutdownRequest();

    return () => {
      consumptionsRef.current?.abort();
      alertsRef.current?.abort();
      bleRef.current?.abort();
      gwActsRef.current?.abort();
      subGwActsRef.current?.abort();
      shutdownReqRef.current?.abort();
    };
  }, [realId]);

  /* Live update: refresh page data when a notification arrives for this meter.
   * Every notification carries the meterId it concerns, so anything for another
   * meter is dropped here and handled by the header's notification dropdown. */
  useEffect(() => {
    if (!OBJECT_ID_REGEX.test(realId)) return;
    const sse = subscribeEvents('/api/notifications', {
      onmessage: event => {
        try {
          const data = JSON.parse(event.data);
          if (data.meterId !== realId) return;

          /* Refresh the alert list and the status the colours are drawn from */
          fetchAlerts();
          fetchWaterMeterStatus();

          /* A shutdown request pops the modal straight away — the same fetch
           * the dropdown's openShutdown state triggers, without the round-trip
           * through navigate() that a page already on screen does not need. */
          if (data.event === 'shutdown') {
            fetchShutdownRequest();
          }
        } catch (_) {}
      },
      onerror: () => {}
    });
    return () => sse?.abort();
  }, [realId]);

  useEffect(() => {
    return () => {
      saveRef.current?.abort();
      shutdownRespRef.current?.abort();
      deleteRef.current?.abort();
    };
  }, []);

  /* Open shutdown modal when navigated here from a shutdown notification,
   * or when the notification arrives while already on this page (SSE sets
   * openShutdown via navigate state).
   * Waits for realId to become a valid ObjectId before calling fetchShutdownRequest
   * so the request uses the correct backend ID, not the raw URL param. */
  useEffect(() => {
    if (!location.state?.openShutdown) return;
    if (!OBJECT_ID_REGEX.test(realId)) return; // realId not resolved yet — wait
    fetchShutdownRequest();
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state?.openShutdown, realId]);

  if (error && !waterMeter) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!waterMeter) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  const statusColor = waterMeter.criticalState ? 'red' : waterMeter.deviceStatus === 'Up' ? 'green' : 'gray';

  return (
    <Box p={2}>
      <IconButton onClick={() => navigate(-1)}>
        <ArrowBackIcon />
      </IconButton>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          {waterMeter.deviceName || `W-${waterMeter.serialNumber}`}
        </Typography>
        <FiberManualRecordIcon sx={{ color: statusColor, ml: 1 }} fontSize="large" />
      </Box>

      <Grid container spacing={2} sx={{
        [theme.breakpoints.up('md')]: {
          height: 'calc(100vh - 100px)',
        },
      }}>
        {/* Left column */}
        <Grid item xs={12} sm={12} md={4} sx={{
          display: 'flex',
          flexDirection: 'column',
          [theme.breakpoints.up('md')]: { height: '100%' }
        }}>
          <Paper sx={{ p: 2, mb: 2, flexShrink: 0 }}>
            {!editMode ? (
              <Box>
                <Typography noWrap sx={{ maxWidth: 'auto' }}>Device Id: {waterMeter._id}</Typography>
                <Typography>
                  Last Seen:{' '}
                  {waterMeter.lastseen
                    ? formatDate(waterMeter.lastseen, true)
                    : 'Never'}
                </Typography>
                <Typography>BLE Address: {waterMeter.bleAddress}</Typography>
                <Typography>
                  Serial Number: {waterMeter.deviceName.replace(/^W-/, '')}
                </Typography>
                <Typography>
                  Total Consumption: {formatConsumption(waterMeter.totalConsumption)}
                </Typography>
                <Typography>
                  Registered At: {formatDate(waterMeter.registeredAt)}
                </Typography>
                {waterMeter.lastGatewayId && (
                  <Typography noWrap sx={{ maxWidth: 'auto' }}>
                    Last Gateway Id: {waterMeter.lastGatewayId}
                  </Typography>
                )}
                {formData.gpsCoordinates && (
                  <Typography>GPS Location: {formData.gpsCoordinates}</Typography>
                )}
                <Typography>
                  Device Status: {waterMeter.deviceStatus}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Button onClick={handleEdit}>Edit</Button>
                  {!waterMeter.lastseen && (
                    <Button color="error" onClick={handleDeleteClick}>
                      Delete
                    </Button>
                  )}
                </Box>
              </Box>
            ) : (
              <Box>
                <TextField
                  label="GPS Coordinates"
                  fullWidth
                  value={formData.gpsCoordinates}
                  onChange={e => setFormData({ ...formData, gpsCoordinates: e.target.value })}
                  onBlur={e => setFormData({ ...formData, gpsCoordinates: formatGps(e.target.value) })}
                  helperText="[longitude.xxxxxx, latitude.xxxxxx]"
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Init Pairing Key (hex)"
                  fullWidth
                  autoComplete="off"
                  inputProps={{ autoComplete: 'off' }}
                  value={formData.initPairingKey}
                  onChange={e => {
                    setFormData({ ...formData, initPairingKey: e.target.value.toUpperCase() });
                    setFormErrors({ ...formErrors, initPairingKey: '' });
                  }}
                  error={!!formErrors.initPairingKey}
                  helperText={formErrors.initPairingKey || 'Leave blank to keep auth unchanged'}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Meter Password"
                  type="password"
                  fullWidth
                  autoComplete="new-password"
                  inputProps={{ autoComplete: 'new-password' }}
                  value={formData.password}
                  onChange={e => {
                    setFormData({ ...formData, password: e.target.value });
                    setFormErrors({ ...formErrors, password: '' });
                  }}
                  error={!!formErrors.password}
                  helperText={formErrors.password || 'Leave blank to keep auth unchanged'}
                  sx={{ mb: 2 }}
                />
                <Button onClick={handleSave}>Save</Button>
                <Button onClick={() => setEditMode(false)}>Cancel</Button>
                <Button
                  color="warning"
                  sx={{ mt: 1 }}
                  onClick={() => setResetConsumptionModalOpen(true)}
                >
                  Reset Consumption
                </Button>
              </Box>
            )}
          </Paper>

          {consumptions.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                Consumption Records
              </Typography>
              <Paper sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
                <Grid container sx={{ fontWeight: 'bold', mb: 1 }}>
                  <Grid item xs={6}><Typography>Record</Typography></Grid>
                  <Grid item xs={6}><Typography>Timestamp</Typography></Grid>
                </Grid>
                {consumptions.map(rec => (
                  <Grid container key={rec._id} sx={{ mb: 0.5 }}>
                    <Grid item xs={6}>
                      <Typography>#{formatConsumption(rec.record)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>{formatDate(rec.timestamp)}</Typography>
                    </Grid>
                  </Grid>
                ))}
                <Box textAlign="center" mt={1}>
                  <Button
                    variant="outlined"
                    onClick={() => fetchConsumptions(consumpStart)}
                    disabled={loadingMoreConsumptions}
                  >
                    {loadingMoreConsumptions ? 'Loading…' : 'Load More'}
                  </Button>
                </Box>
              </Paper>
            </>
          )}
        </Grid>

        {/* Right column */}
        <Grid item xs={12} sm={12} md={8} sx={{
          display: 'flex',
          flexDirection: 'column',
          [theme.breakpoints.up('md')]: { height: '100%' }
        }}>
          {/* Critical Alerts */}
          {alerts.length > 0 && (
            <>
              <Typography
                variant="h6"
                sx={
                  waterMeter.criticalState
                    ? {
                        border: '2px red',
                        borderRadius: 1,
                        p: 1,
                        animation: 'borderPulse 1.5s infinite',
                        '@keyframes borderPulse': {
                          '0%,100%': { borderColor: 'transparent', backgroundColor: 'transparent' },
                          '50%': { borderColor: 'rgba(255,0,0,0.3)', backgroundColor: 'rgba(255,0,0,0.3)' }
                        }
                      }
                    : { mb: 1 }
                }
              >
                Critical Alerts
              </Typography>
              <Paper sx={{ p: 1, mb: 2, flexShrink: 0 }}>
                <Grid container sx={{ fontWeight: 'bold', mb: 1 }}>
                  <Grid item xs={isSmall ? 5 : 3}><Typography>Type</Typography></Grid>
                  <Grid item xs={3}><Typography>Status</Typography></Grid>
                  <Grid item xs={isSmall ? 4 : 3}><Typography>Received</Typography></Grid>
                  {!isSmall && <Grid item xs={3}><Typography>Updated</Typography></Grid>}
                </Grid>
                {alerts.map((a, i) => (
                  <Grid container key={i} sx={{ mb: 0.5 }}>
                    <Grid item xs={isSmall ? 5 : 3}><Typography>{a.type}</Typography></Grid>
                    <Grid item xs={3}><Typography>{a.status}</Typography></Grid>
                    <Grid item xs={isSmall ? 4 : 3}><Typography>{formatDate(a.receivedAt)}</Typography></Grid>
                    {!isSmall && <Grid item xs={3}><Typography>{formatDate(a.updatedAt)}</Typography></Grid>}
                  </Grid>
                ))}
              </Paper>
            </>
          )}

          {/* BLE Activities */}
          {bleActivities.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                BLE Activities
              </Typography>
              <Paper sx={{ p: 1, mb: 2, flexShrink: 0 }}>
                <Grid container sx={{ fontWeight: 'bold', mb: 1 }}>
                  <Grid item xs={6}><Typography>Gateway</Typography></Grid>
                  <Grid item xs={4}><Typography>Timestamp</Typography></Grid>
                  <Grid item xs={2}><Typography>RSSI</Typography></Grid>
                </Grid>
                {bleActivities.map((b, i) => (
                  <Grid
                    container
                    key={i}
                    sx={{ mb: 0.5, cursor: 'pointer' }}
                    onClick={() => navigate(`/gateways/${b.gatewayId}`)}
                  >
                    <Grid item xs={6}>
                      <Typography noWrap sx={{ maxWidth: 'auto' }}>
                        {b.gatewayId}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography>{formatDate(b.timestamp)}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography sx={{
                        color:
                          Math.trunc(b.rssi) > -60 ? 'green' :
                          Math.trunc(b.rssi) > -80 ? 'orange' :
                          'red'
                      }}>
                        {Math.trunc(b.rssi)}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </Paper>
            </>
          )}

          {/* Gateway Activities & Subview */}
          {selectedGw ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }} noWrap>
                  Activities for Gateway {selectedGw}
                </Typography>
                <Button onClick={() => setSelectedGw(null)}>Back</Button>
              </Box>
              <Paper sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2 }}>
                <Grid container sx={{ fontWeight: 'bold', mb: 1 }}>
                  <Grid item xs={3}><Typography>Gateway</Typography></Grid>
                  <Grid item xs={3}><Typography>Topic</Typography></Grid>
                  <Grid item xs={3}><Typography>Action</Typography></Grid>
                  <Grid item xs={3}><Typography>Timestamp</Typography></Grid>
                </Grid>
                {subGwActs.map((g, i) => (
                  <Grid container key={i} sx={{ mb: 0.5 }}>
                    <Grid item xs={3}>
                      <Typography noWrap sx={{ maxWidth: 'auto' }}>
                        {g.gatewayId}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography noWrap sx={{ maxWidth: 'auto' }}>
                        {g.mqttTopic || 'NA'}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography noWrap sx={{ maxWidth: 'auto' }}>
                        {g.action}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography>{formatDate(g.timestamp)}</Typography>
                    </Grid>
                  </Grid>
                ))}
                <Box textAlign="center" mt={1}>
                  <Button
                    variant="outlined"
                    onClick={() => fetchSubGwActs(selectedGw, subStart)}
                    disabled={loadingMoreSub}
                  >
                    {loadingMoreSub ? 'Loading…' : 'Load More'}
                  </Button>
                </Box>
              </Paper>
            </>
          ) : (
            gwActs.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom>
                  Gateway Activities
                </Typography>
                <Paper sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
                  <Grid container sx={{ fontWeight: 'bold', mb: 1 }}>
                    <Grid item xs={3}><Typography>Gateway</Typography></Grid>
                    <Grid item xs={3}><Typography>Topic</Typography></Grid>
                    <Grid item xs={3}><Typography>Action</Typography></Grid>
                    <Grid item xs={3}><Typography>Timestamp</Typography></Grid>
                  </Grid>
                  {gwActs.map((g, i) => (
                    <Grid
                      container
                      key={i}
                      sx={{ mb: 0.5, cursor: 'pointer' }}
                      onClick={() => handleGwRowClick(g.gatewayId)}
                    >
                      <Grid item xs={3}>
                        <Typography noWrap sx={{ maxWidth: 'auto' }}>
                          {g.gatewayId}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography noWrap sx={{ maxWidth: 'auto' }}>
                          {g.mqttTopic || 'NA'}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography noWrap sx={{ maxWidth: 'auto' }}>
                          {g.action}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography>{formatDate(g.timestamp)}</Typography>
                      </Grid>
                    </Grid>
                  ))}
                  <Box textAlign="center" mt={1}>
                    <Button
                      variant="outlined"
                      onClick={() => fetchGwActs(gwStart)}
                      disabled={loadingMoreGw}
                    >
                      {loadingMoreGw ? 'Loading…' : 'Load More'}
                    </Button>
                  </Box>
                </Paper>
              </>
            )
          )}
        </Grid>
      </Grid>

      {/* Shutdown Modal */}
      <Modal open={shutdownModalOpen} onClose={() => setShutdownModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            bgcolor: 'background.paper',
            p: 2
          }}
        >
          <Typography variant="h6">Shutdown Requested</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={() => setShutdownModalOpen(false)}>Close</Button>
            <Button color="error" onClick={() => handleShutdownResponse('rejected')}>Reject</Button>
            <Button onClick={() => handleShutdownResponse('confirmed')}>Confirm</Button>
          </Box>
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            bgcolor: 'background.paper',
            p: 2
          }}
        >
          <Typography variant="h6">Confirm Delete</Typography>
          <Typography>Are you sure you want to delete this device?</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button color="error" onClick={handleConfirmDelete}>Delete</Button>
          </Box>
        </Box>
      </Modal>

      {/* Reset Consumption Confirmation Modal */}
      <Modal open={resetConsumptionModalOpen} onClose={() => setResetConsumptionModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            bgcolor: 'background.paper',
            p: 2
          }}
        >
          <Typography variant="h6">Reset Consumption</Typography>
          <Typography>
            This will delete all consumption records and set the total to 0. This cannot be undone.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={() => setResetConsumptionModalOpen(false)}>Cancel</Button>
            <Button
              color="warning"
              onClick={async () => {
                setResetConsumptionModalOpen(false);
                try {
                  await api.delete(`/api/proxy/wmeters/consumption/${realId}`);
                  setWaterMeter(prev => ({ ...prev, totalConsumption: 0 }));
                  setConsumptions([]);
                  setSnackbar({ open: true, message: 'Consumption reset to 0', severity: 'success' });
                } catch (err) {
                  setSnackbar({ open: true, message: 'Reset failed', severity: 'error' });
                }
              }}
            >
              Reset
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}